import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faChartLine,
  faChalkboardTeacher,
  faTree,
  faServer,
  faNetworkWired
} from "@fortawesome/free-solid-svg-icons";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 8rem 2rem;
  background: transparent;
  color: #e8e8f0;
  position: relative;
`;

const Title = styled.h2`
  font-size: clamp(2rem, 4vw, 2.5rem);
  margin-bottom: 5rem;
  text-align: center;
  color: white;
  font-weight: 800;
  font-family: "Outfit", "Inter", sans-serif;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  position: relative;
  z-index: 1;

  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, #4AE290, #4A90E2);
    border-radius: 2px;
  }
`;

const TimelineContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
  max-width: 900px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 45px;
    top: 20px;
    bottom: 20px;
    width: 2px;
    background: linear-gradient(180deg, rgba(74, 144, 226, 0) 0%, rgba(74, 144, 226, 0.4) 15%, rgba(144, 19, 254, 0.4) 85%, rgba(144, 19, 254, 0) 100%);
    z-index: 0;

    @media (max-width: 768px) {
      left: 30px;
    }
  }
`;

const ExperienceCard = styled.div`
  display: flex;
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(20px) saturate(180%);
  border-radius: 20px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${fadeIn} 0.5s ease-out backwards;
  animation-delay: ${props => props.index * 0.15}s;
  border: 1px solid rgba(255, 255, 255, 0.03);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  padding: 2rem;
  position: relative;
  z-index: 1;
  overflow: hidden;

  /* Premium "Machined Glass" Inner Border */
  &::after {
    content: '';
    position: absolute;
    inset: 1px;
    border-radius: 19px;
    box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.15);
    pointer-events: none;
    z-index: 2;
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 4px;
    background: linear-gradient(180deg, ${props => props.color} 0%, rgba(255,255,255,0) 100%);
    opacity: 0.5;
    transition: width 0.3s ease;
  }

  &:hover {
    transform: translateX(10px);
    background: rgba(255, 255, 255, 0.04);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.4), 0 0 40px ${props => props.color}15;
    border-color: rgba(255, 255, 255, 0.08);
    
    &::before {
      width: 10px;
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 1.5rem;
    padding-left: 2.5rem;
  }
`;

const IconWrapper = styled.div`
  font-size: 1.5rem;
  color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid ${props => props.color}40;
  flex-shrink: 0;
  margin-right: 2rem;
  box-shadow: 0 0 20px ${props => props.color}20;

  @media (max-width: 768px) {
    position: absolute;
    left: 10px;
    top: 24px;
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const HeaderGroup = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.25rem;
    margin-left: 2rem;
  }
`;

const CompanyName = styled.h3`
  font-size: 1.4rem;
  color: #fff;
  font-family: "Outfit", "Inter", sans-serif;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const RoleTitle = styled.h4`
  font-size: 1.1rem;
  color: ${props => props.color};
  font-weight: 500;
  margin-bottom: 1rem;
  font-family: "Inter", sans-serif;

  @media (max-width: 768px) {
    margin-left: 2rem;
  }
`;

const DateRange = styled.span`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.5);
  font-family: "Inter", monospace;
  background: rgba(255, 255, 255, 0.05);
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  white-space: nowrap;
`;

const BulletPoints = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

const BulletItem = styled.li`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 0.5rem;
  padding-left: 1.5rem;
  position: relative;
  font-family: "Inter", sans-serif;

  &::before {
    content: '→';
    position: absolute;
    left: 0;
    color: ${props => props.color};
    opacity: 0.8;
  }

  @media (max-width: 768px) {
    padding-left: 1rem;
  }
`;

const Work = () => {
  const experiences = [
    {
      company: "AXA XL",
      role: "AI Engineering Team | AI Strategy & Data Engineering",
      date: "Recent",
      color: "#4A90E2",
      icon: faBuilding,
      points: [
        "Developed and implemented advanced machine learning models (Python, TensorFlow) to enhance risk detection and fraud prevention.",
        "Managed data pipeline architecture using SQL to ensure data integrity for regulatory reporting.",
        "Collaborated with compliance teams to integrate AI models meeting strict financial regulatory standards.",
        "Championed Agile methodologies to rapidly iterate and deploy AI-powered tools for risk assessment."
      ]
    },
    {
      company: "The Swing Suite LTD",
      role: "Director",
      date: "2020 – Present",
      color: "#9013FE",
      icon: faChartLine,
      points: [
        "Designed and executed data strategy using Python and TensorFlow to model customer behaviours.",
        "Developed a custom data-rich booking platform integrating real-time dashboards and operational analytics.",
        "Applied SQL and Excel to derive customer lifecycle insights, increasing revenue via targeted upselling.",
        "Led cybersecurity enhancements and system architecture reviews for scalable strategic risk reduction."
      ]
    },
    {
      company: "Royal Tutors",
      role: "Head of IT & Digital Strategy",
      date: "2019 – 2020",
      color: "#E2A44A",
      icon: faChalkboardTeacher,
      points: [
        "Led the strategic rollout of immersive VR learning tools, assessing impact via Tableau analytics.",
        "Managed business-wide database optimizations aligning data systems with operational objectives.",
        "Championed cross-functional Agile project delivery to implement digital changes efficiently."
      ]
    },
    {
      company: "Kaiteriteri Recreation (NZ)",
      role: "Web Development & Marketing Manager",
      date: "2017 – 2019",
      color: "#4AE290",
      icon: faTree,
      points: [
        "Transformed online presence through a data-led web strategy, systematically increasing tourism revenue.",
        "Devised and iterated targeted marketing campaigns utilizing Google Analytics segments.",
        "Integrated digital activity with broader commercial strategy to heavily boost cross-channel conversion."
      ]
    },
    {
      company: "Wincor-Nixdorf",
      role: "Senior Deployment Engineer (Tesco 2020 Project)",
      date: "2015 – 2017",
      color: "#E24A63",
      icon: faServer,
      points: [
        "Used deployment analytics to enhance hardware utilization across 2,000+ sites, increasing cost savings.",
        "Supported massive national rollouts and liaised tightly with stakeholders to meet strategic deadlines."
      ]
    },
    {
      company: "Cloud Resolutions Ltd",
      role: "Systems Administrator & Deployment Lead",
      date: "2013 – 2017",
      color: "#4AE2D3",
      icon: faNetworkWired,
      points: [
        "Managed technical delivery for complex IT projects, using operational BI to guide workforce allocation.",
        "Explored early use of AI in logistical planning, laying the foundational groundwork for later automation."
      ]
    }
  ];

  return (
    <Container id="experience">
      <Title>Professional Experience</Title>
      <TimelineContainer>
        {experiences.map((exp, index) => (
          <ExperienceCard key={index} index={index} color={exp.color}>
            <IconWrapper color={exp.color}>
              <FontAwesomeIcon icon={exp.icon} />
            </IconWrapper>
            <ContentWrapper>
              <HeaderGroup>
                <CompanyName>{exp.company}</CompanyName>
                <DateRange>{exp.date}</DateRange>
              </HeaderGroup>
              <RoleTitle color={exp.color}>{exp.role}</RoleTitle>
              <BulletPoints>
                {exp.points.map((pt, i) => (
                  <BulletItem key={i} color={exp.color}>{pt}</BulletItem>
                ))}
              </BulletPoints>
            </ContentWrapper>
          </ExperienceCard>
        ))}
      </TimelineContainer>
    </Container>
  );
};

export default Work;
